import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Button, SearchField, Toolbar, VFlex } from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconFilterLine,
  IconPageNextLine,
  IconPagePreviousLine,
  IconRejectLine,
  IconSearchLine,
} from "@examspark/common-ui/dist/components/icons";

import { BrowserContext } from "../../../../providers/BrowserContext";
import SearchContext from "../../../../providers/SearchContext";
import { useElementSize } from "../hooks/useElementSize";

import ActivityEditorItemPickerModalItem from "./ItemPickerModalItem";

const Styled = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  box-sizing: border-box;
  min-width: 0;
`;
const StyledFilters = styled.div`
  flex: 1;
  max-width: 25rem;
  padding: 1rem;
  overflow: auto;
  border-right: solid 2px #ccc;
`;
const StyledFiltersNarrow = styled.div`
  flex: 1;
  overflow: auto;
`;
const StyledResultsCountLabel = styled.span`
  margin-left: 0.5rem;
  color: #555;
`;

const StyledResults = styled.div`
  flex: 1.5;
  min-width: 0;
`;

const ToolbarPane = styled(Toolbar)`
  padding: 1rem;
  /* background-color: #f7f7f7; */
  border-bottom: solid 1px #ddd;
`;

/**
 * Responsible for rendering search results, a search toolbar, and sidebar
 * filter interfaces
 */
const ItemPickerMainLayout = (props: { filters: React.ReactNode }) => {
  const search = useContext(SearchContext);
  const [browser, dispatchBrowser] = useContext(BrowserContext);
  const [searchString, setSearchString] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const size = useElementSize(ref);

  const [showFilters, setShowFilters] = useState(true);
  const isNarrow = (size?.width ?? 0) < 500;

  useEffect(() => {
    if (isNarrow) {
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  }, [isNarrow]);

  return (
    <Styled ref={ref}>
      {isNarrow && showFilters ? (
        <>
          <StyledFiltersNarrow>
            <ToolbarPane>
              <Button
                title="Back to Results"
                icon={<IconPagePreviousLine />}
                variant="outlined"
                onClick={() => setShowFilters(false)}
              >
                Back to Results
              </Button>
            </ToolbarPane>
            <div style={{ padding: "1rem" }}>{props.filters}</div>
          </StyledFiltersNarrow>
        </>
      ) : (
        <>
          {showFilters && !isNarrow && (
            <StyledFilters
              // firefox likes to focus on scrollable elements. Since everything is
              // interactable in here, we don't need that
              tabIndex={-1}
            >
              {props.filters}
            </StyledFilters>
          )}
          <StyledResults>
            <VFlex>
              <ToolbarPane>
                <Button
                  // this id is used for the skip-to-content button
                  // because passing a ref to the button is hard
                  id="item-picker-filters"
                  title="Toggle Filters"
                  icon={<IconFilterLine />}
                  variant={showFilters ? "contained" : "outlined"}
                  color={showFilters ? "primary" : "secondary"}
                  onClick={() => setShowFilters(!showFilters)}
                />
                <div style={{ flex: "1" }}>
                  <SearchField
                    placeholder="Search by Name"
                    value={searchString}
                    onChange={(value) => {
                      setSearchString(value);
                    }}
                    onEnterKeyPress={() => {
                      if (search?.state.filter.query !== searchString) {
                        search?.dispatch({
                          type: "UPDATE",
                          data: {
                            query: searchString,
                          },
                        });
                      } else {
                        search?.refetch();
                      }
                    }}
                  />
                </div>
                <Button
                  title="Clear Search"
                  variant="outlined"
                  onClick={() => search?.dispatch({ type: "RESET" })}
                  icon={<IconRejectLine />}
                  disabled={search?.isFetching}
                />
                <Button
                  title=""
                  variant="outlined"
                  onClick={async (e) => {
                    if (e.altKey) {
                      console.log("search", search);
                      return;
                    }

                    // ensure we update state with the latest search string first
                    // this will also refetch since the query has changed
                    if (search?.state.filter.query !== searchString) {
                      search?.dispatch({
                        type: "UPDATE",
                        data: {
                          query: searchString,
                        },
                      });
                    } else {
                      search?.query.remove();
                      search?.facets?.remove();
                      const [results, facets] = (await search?.refetch()) ?? [];
                      if (e.altKey) {
                        console.log("results", results);
                        console.log("facets", facets);
                      }
                    }
                  }}
                  isLoading={search?.isFetching}
                  icon={<IconSearchLine />}
                />
              </ToolbarPane>
              <ToolbarPane>
                <Button
                  title="Previous Page"
                  variant="outlined"
                  onClick={() => search?.query.previousPage()}
                  disabled={!search?.query.canPagePrevious}
                  icon={<IconPagePreviousLine />}
                />
                <Button
                  title="Next Page"
                  variant="outlined"
                  disabled={!search?.query.canPageNext}
                  onClick={() => search?.query.nextPage()}
                  icon={<IconPageNextLine />}
                />
                <Button
                  title="Select All"
                  variant="outlined"
                  onClick={() => {
                    dispatchBrowser({
                      type: "ADD_SELECTED",
                      data: search?.query.currentData?.items ?? [],
                    });
                  }}
                  icon={<IconAcceptLine />}
                >
                  All
                </Button>
                <Button
                  title="Select None"
                  variant="outlined"
                  onClick={() =>
                    dispatchBrowser({
                      type: "REMOVE_SELECTED",
                      data: search?.query.currentData?.items ?? [],
                    })
                  }
                  icon={<IconRejectLine />}
                >
                  None
                </Button>
                <Toolbar.Flex />
                <div>
                  Page {search?.query.currentPage}{" "}
                  <StyledResultsCountLabel>
                    ({search?.query.totalCount} Results)
                  </StyledResultsCountLabel>
                </div>
              </ToolbarPane>
              <VFlex.Main data-cy="item-picker-results">
                {search?.query.currentData?.items?.map((result) => {
                  return (
                    <ActivityEditorItemPickerModalItem
                      key={result.id}
                      selected={browser.focused === result.id}
                      checked={
                        !!browser.selected.find(
                          (item) => item.id === result.id,
                        ) ||
                        !!browser.selectedReadOnly.find(
                          (item) => item.id === result.id,
                        )
                      }
                      onToggleChecked={(item) => {
                        dispatchBrowser({
                          type: "TOGGLE_SELECTED",
                          data: item,
                        });
                      }}
                      disabled={
                        !!browser.selectedReadOnly.find(
                          (item) => item.id === result.id,
                        )
                      }
                      item={result}
                      onClick={() =>
                        dispatchBrowser({
                          type: "SET_FOCUSED",
                          data: result,
                        })
                      }
                    />
                  );
                })}
              </VFlex.Main>
            </VFlex>
          </StyledResults>
        </>
      )}
    </Styled>
  );
};

export default ItemPickerMainLayout;
